import React, { useEffect, useState, useRef, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../firebase";
import CenterSpinner from "./CenterSpinner";
import { apiService } from "../services/apiService";
import {
  BLOCKED_ACCOUNT_MESSAGE,
  SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE,
} from "../utils/loginAuthErrors";
import type { StudioAccessStatus } from "../services/api/access";

interface ProtectedRouteProps {
  children: ReactNode;
  unauthenticatedTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  unauthenticatedTo = "/login",
}) => {
  const { user, loading, logout, blockCheckVerified } = useAuth();
  const location = useLocation();
  const [checkingBlocked, setCheckingBlocked] = useState(false);
  const [accessStatus, setAccessStatus] = useState<
    StudioAccessStatus | "checking" | "unavailable"
  >("checking");
  const [permissionScope, setPermissionScope] = useState<
    "full" | "guest_sample" | "none" | "checking"
  >("checking");
  const hasCheckedRef = useRef(false);

  // 사용자 차단 여부 확인 (AuthProvider에서 이미 확인한 경우 스킵)
  useEffect(() => {
    const checkUserBlocked = async (): Promise<void> => {
      if (blockCheckVerified) return;
      if (
        !user ||
        loading ||
        checkingBlocked ||
        hasCheckedRef.current ||
        auth.currentUser?.uid !== user.uid
      ) {
        return;
      }

      hasCheckedRef.current = true;
      setCheckingBlocked(true);

      try {
        const token =
          localStorage.getItem("firebaseToken") ||
          sessionStorage.getItem("firebaseToken");

        if (!token) {
          setCheckingBlocked(false);
          hasCheckedRef.current = false; // 토큰이 없으면 다음에 다시 확인
          return;
        }

        // 사용자 정보 조회
        const userInfo = await apiService.getUserInfo(token);

        // 엔드포인트가 없거나 응답이 없으면 차단 확인 건너뜀
        if (!userInfo) {
          setCheckingBlocked(false);
          hasCheckedRef.current = false; // 응답이 없으면 다음에 다시 확인
          return;
        }

        // 사용자가 차단되었는지 확인
        const isBlocked =
          (
            userInfo as {
              user?: { isBlocked?: boolean | string };
              isBlocked?: boolean | string;
            }
          )?.user?.isBlocked ||
          (userInfo as { isBlocked?: boolean | string })?.isBlocked;
        if (isBlocked === true || isBlocked === "true") {
          console.log("🚫 ProtectedRoute: 사용자가 차단됨 - 로그아웃 처리");

          sessionStorage.setItem(
            "loginToast",
            JSON.stringify({
              type: "error",
              message: BLOCKED_ACCOUNT_MESSAGE,
            }),
          );

          logout();
          window.location.href = "/login";
          return;
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        const isSocialConflict =
          errMsg.includes("409") ||
          errMsg.includes(SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE);
        if (isSocialConflict) {
          sessionStorage.setItem(
            "loginToast",
            JSON.stringify({
              type: "error",
              message: SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE,
            }),
          );
          logout();
          window.location.href = "/login";
          return;
        }
        console.error("❌ ProtectedRoute: 사용자 정보 조회 실패:", error);
        // 오류 발생 시 다음에 다시 확인할 수 있도록
        hasCheckedRef.current = false;
      } finally {
        setCheckingBlocked(false);
      }
    };

    checkUserBlocked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, blockCheckVerified]);

  useEffect(() => {
    let active = true;

    const checkStudioAccess = async (): Promise<void> => {
      if (!user || loading) {
        if (active) setAccessStatus("checking");
        if (active) setPermissionScope("checking");
        return;
      }
      if (user.isGuest) {
        if (active) setAccessStatus("approved");
        if (active) setPermissionScope("guest_sample");
        return;
      }
      if (auth.currentUser?.uid !== user.uid) {
        if (active) setAccessStatus("checking");
        return;
      }

      try {
        const status = await apiService.getAccessStatus();
        // AccessRequestPage is the single owner of POST /api/access/requests.
        // Protected routes only enforce admission and redirect non-members.
        if (active) {
          setAccessStatus(status.accessStatus);
          setPermissionScope(status.permissionScope ?? "none");
        }
      } catch (error) {
        console.error("Studio access status check failed:", error);
        if (active) setAccessStatus("unavailable");
      }
    };

    checkStudioAccess();
    return () => {
      active = false;
    };
  }, [user, loading]);

  if (loading || checkingBlocked || (user && accessStatus === "checking")) {
    return (
      <>
        <CenterSpinner
          radius={35}
          thickness={10}
          ariaLabel="Loading"
          speedSec={1.4}
          color="#50B786"
          trackColor="rgba(80, 183, 134, 0.3)"
          arcRatio={0.2}
        />
      </>
    );
  }

  if (!user) {
    if (process.env.NODE_ENV === "development") {
      console.log(
        "🔐 ProtectedRoute: 인증되지 않은 사용자 - 로그인 페이지로 리디렉션",
      );
    }
    return <Navigate to={unauthenticatedTo} replace />;
  }

  if (user.isGuest) {
    const editorState = (location.state ?? {}) as { isSampleFile?: boolean };
    if (location.pathname === "/editor" && editorState.isSampleFile !== true) {
      return <Navigate to="/home" replace />;
    }
  } else if (accessStatus !== "approved" || permissionScope !== "full") {
    return <Navigate to="/access" replace />;
  }

  if (process.env.NODE_ENV === "development") {
    console.log("✅ ProtectedRoute: 인증된 사용자 - 접근 허용");
  }
  return <>{children}</>;
};

export default ProtectedRoute;
